import api from 'services/api';
import { REJECT, array, nullable, num, numeric, object, str, type Received } from 'validation';

import { folderPathSchema, type FolderPath } from 'api/shared/folderPath';
import { filterConfigSchema, listColumnConfigSchema, type FilterConfig, type ListColumnConfig } from 'api/shared/listConfig';
import { readResponse } from 'api/shared/readResponse';

// GET /locks — the list read. Backend: routes/locks.js `GET /locks` →
// `getLocks` (modules/generic_functions/locks/getLocks.js) for the paginated
// rows, then the route enriches each row with `folder_path` (buildFolderPath)
// and wraps them in `{ locks, allowedZones, config, filtersConfig, pagination }`.
//
// `LockRow` is a SEPARATE type from the single-GET `Lock`, even though the
// scalar columns match: the two come from different code paths (the list route
// adds `folder_path`; the single-GET does not) — equal-by-coincidence, not
// identity (see ../../README.md).
//
// Supported query parameters (all optional):
//   search       ILIKE '%search%' on name OR zone name OR description
//   name         ILIKE '%name%' on the lock name alone — what a typeahead wants, since then every
//                row shown contains what was typed
//   id           exact equals (CSV accepted) — resolves a stored id back to its row
//   zone_name    exact equals (CSV accepted)
//   limit_value  exact equals on the lock's `limit` column (a list-page filter, not a picker one)
//   limit        page size; the backend default is 50 and no maximum is enforced
//   offset       0-based row offset. `page` (1-based) is accepted too, and `offset` wins when both
//                are sent
//   sortColumn   the backend's allow-list, plus the `zone_name` alias it maps to `z.name`. An
//                unknown column is NOT an error: the backend logs a warning and falls back to
//                'name'. The default is already 'name' ASC, so an unsorted read cannot happen here
//   sortOrder    'ASC' | 'DESC'
export interface LockRow {
  readonly id: number;
  readonly name: string;
  readonly description: string | null;
  readonly folder_id: number | null;
  readonly zone_name: string;
  readonly limit: number;
  readonly limit_allocation: number;
  readonly created_at: string;
  readonly updated_at: string;
  // Enriched per-row by the list route (null when the lock has no folder).
  readonly folder_path: FolderPath | null;
}

interface Pagination {
  readonly total_records: number;
  readonly total_pages: number;
  readonly current_page: number;
  readonly limit: number;
  readonly offset: number;
}

export interface LocksPage {
  readonly locks: readonly LockRow[];
  readonly pagination: Pagination;
  // Zone names the user may list, for the zone filter dropdown.
  readonly allowedZones: readonly string[];
  // List-table config, shared across list endpoints (see api/shared/listConfig).
  readonly config: readonly ListColumnConfig[];
  readonly filtersConfig: readonly FilterConfig[];
}

const rowSchema = object<LockRow>({
  id: num(REJECT),
  name: str(),
  description: nullable(str()),
  folder_id: nullable(num()),
  zone_name: str(),
  limit: num(),
  limit_allocation: num(),
  created_at: str(),
  updated_at: str(),
  folder_path: nullable(folderPathSchema),
});

// COUNT-derived totals can arrive as numeric strings from Postgres; `numeric()`
// normalizes both number and string.
const paginationSchema = object<Pagination>({
  total_records: numeric(),
  total_pages: numeric(),
  current_page: numeric(),
  limit: numeric(),
  offset: numeric(),
});

const pageSchema = object<LocksPage>({
  locks: array(rowSchema),
  pagination: paginationSchema,
  allowedZones: array(str()),
  config: array(listColumnConfigSchema),
  filtersConfig: array(filterConfigSchema),
});

export interface LocksQuery {
  readonly search?: string;
  readonly name?: string;
  readonly id?: number;
  readonly zone_name?: string;
  readonly limit_value?: number;
  readonly limit?: number;
  readonly offset?: number;
  readonly page?: number;
  readonly sortColumn?: 'id' | 'name' | 'limit' | 'created_at' | 'updated_at' | 'zone_name';
  readonly sortOrder?: 'ASC' | 'DESC';
}

/**
 * GET `/locks`. Returns the full list page (rows + pagination + list config).
 * Throws on transport error or structural validation failure of the response.
 */
export const getLocks = async (params: LocksQuery = {}): Promise<Received<LocksPage>> => {
  const res = await api.get('/locks', { params });
  return readResponse(res.data, { schema: pageSchema, source: 'getLocks' });
};
